import { Link } from "react-router-dom";
import { Button, Card, Row, Col, Dropdown, Modal } from "antd";
import type { MenuProps } from "antd";
import {
  MenuOutlined,
  CloseOutlined,
  AppstoreOutlined,
  ApiOutlined,
  TeamOutlined,
  SafetyOutlined,
  GlobalOutlined,
  RocketOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useState } from "react";
import { useAuthStore } from "../stores/auth";
import logoImg from "@/assets/kimap-logo-dark.png";
import bannerImg1 from "@/assets/banner-1.jpg";

const features = [
  {
    icon: <AppstoreOutlined className="text-4xl text-primary-500" />,
    title: "Intuitive Map Editor",
    description:
      "Create professional indoor maps with our easy-to-use 2D/3D editor",
  },
  {
    icon: <GlobalOutlined className="text-4xl text-primary-500" />,
    title: "3D Visualization",
    description: "View your maps in stunning 3D with realistic rendering",
  },
  {
    icon: <ApiOutlined className="text-4xl text-primary-500" />,
    title: "Powerful API",
    description: "Integrate maps into your applications with our REST API",
  },
  {
    icon: <TeamOutlined className="text-4xl text-primary-500" />,
    title: "Team Collaboration",
    description: "Work together with your team on shared projects",
  },
  {
    icon: <SafetyOutlined className="text-4xl text-primary-500" />,
    title: "Secure & Reliable",
    description: "Enterprise-grade security with automatic backups",
  },
  {
    icon: <RocketOutlined className="text-4xl text-primary-500" />,
    title: "Fast Performance",
    description: "Optimized for speed with cloud-based rendering",
  },
];

const plans = [
  { name: "Free", price: 0, maps: 1, storage: "100MB", highlight: false },
  { name: "Personal", price: 299, maps: 3, storage: "500MB", highlight: false },
  {
    name: "Professional",
    price: 799,
    maps: 10,
    storage: "2GB",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: 1999,
    maps: "Unlimited",
    storage: "10GB",
    highlight: false,
  },
];

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { token, user, clearAuth } = useAuthStore();

  const isLoggedIn = !!token;

  const handleConsoleClick = () => {
    if (isLoggedIn) {
      navigate("/maps");
    } else {
      navigate("/login");
    }
  };

  const handleLogout = () => {
    Modal.confirm({
      title: t('menu.logoutConfirmTitle'),
      content: t('menu.logoutConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => {
        clearAuth();
        navigate("/");
      },
    });
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "dashboard",
      icon: <AppstoreOutlined />,
      label: t("menu.dashboard") || "Dashboard",
      onClick: () => navigate("/maps"),
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: t("menu.settings") || "Settings",
      onClick: () => navigate("/profile"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout") || "Logout",
      onClick: handleLogout,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* 导航栏 - 黑色样式 */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-[#1f2937] shadow-md">
        <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src={logoImg} alt="Kimap" className="h-5 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-gray-300 hover:text-white text-sm font-medium transition-colors"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="text-gray-300 hover:text-white text-sm font-medium transition-colors"
            >
              Pricing
            </a>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Menu or Auth Buttons */}
            {isLoggedIn ? (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <div className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-700 cursor-pointer transition-colors">
                  <div className="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium overflow-hidden">
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.realName || user.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserOutlined />
                    )}
                  </div>
                </div>
              </Dropdown>
            ) : (
              <Button
                type="primary"
                size="small"
                className="!bg-blue-500 !border-blue-500 hover:!bg-blue-600"
                onClick={handleConsoleClick}
              >
                Console
              </Button>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-1.5 hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <CloseOutlined className="text-white" />
              ) : (
                <MenuOutlined className="text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#1f2937] border-t border-gray-700 shadow-lg">
            <div className="px-4 py-3 space-y-3">
              <a
                href="#features"
                className="block text-gray-300 hover:text-white py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="block text-gray-300 hover:text-white py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              <div className="pt-2 border-t border-gray-700">
                {isLoggedIn ? (
                  <Button
                    block
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/maps");
                    }}
                    className="!bg-blue-500 !text-white"
                  >
                    Console
                  </Button>
                ) : (
                  <Link to="/login" className="block py-2">
                    <Button block className="!border-gray-500 !text-gray-300">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section - Banner with Overlay */}
      <section className="relative pt-14">
        {/* Banner Background */}
        <div className="hero-banner">
          <div className="banner-carousel">
            <div className="banner-slide">
              <img
                src={bannerImg1}
                alt="Indoor Mapping"
                className="banner-image"
              />
            </div>
          </div>
          <div className="banner-overlay"></div>
        </div>

        {/* Hero Content */}
        <div className="hero-container">
          <h1 className="hero-title">Create Stunning Indoor Maps</h1>
          <p className="hero-subtitle">
            The professional 3D indoor mapping platform for architects,
            designers, and businesses
          </p>
          <div className="hero-actions">
            {isLoggedIn ? (
              <Button
                type="primary"
                size="large"
                onClick={() => navigate("/maps")}
                className="!bg-blue-500 hover:!bg-blue-600 !text-white !border-none !font-semibold !h-12 !px-8"
              >
                Console
              </Button>
            ) : (
              <Link to="/register">
                <Button
                  type="primary"
                  size="large"
                  className="!bg-white !text-primary-600 !border-none !font-semibold !h-12 !px-8"
                >
                  Get Started Free
                </Button>
              </Link>
            )}
            <Link to="/editor/example">
              <Button
                size="large"
                className="!bg-green-600 !border-green-600 !text-white hover:!bg-green-600 !h-12 !px-8"
              >
                View Example
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">2D/3D</div>
              <div className="stat-label">Map Editor</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">API</div>
              <div className="stat-label">Integration</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">24/7</div>
              <div className="stat-label">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <div id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2>Powerful Features</h2>
            <p>Everything you need to create professional indoor maps</p>
          </div>
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col span={8} key={index}>
                <Card
                  bordered={false}
                  className="h-full text-center hover:shadow-lg transition-all hover:-translate-y-1"
                >
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500">{feature.description}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-20">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2>Simple Pricing</h2>
            <p>Choose the plan that fits your needs</p>
          </div>
          <Row gutter={[24, 24]} justify="center">
            {plans.map((plan, index) => (
              <Col span={6} key={index}>
                <Card
                  bordered={false}
                  className={`h-full text-center relative ${plan.highlight ? "!border-primary-500 !shadow-lg" : ""}`}
                  style={
                    plan.highlight
                      ? { borderWidth: 2, transform: "scale(1.05)" }
                      : {}
                  }
                >
                  {plan.highlight && (
                    <div className="popular-badge">Recommended</div>
                  )}
                  <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-primary-500 mb-4">
                    ¥{plan.price}
                    {plan.price > 0 && (
                      <span className="text-sm font-normal text-gray-400">
                        /year
                      </span>
                    )}
                  </div>
                  <div className="text-gray-500 mb-4">
                    <div>
                      {plan.maps}{" "}
                      {typeof plan.maps === "number" && plan.maps > 1
                        ? "maps"
                        : "map"}
                    </div>
                    <div>{plan.storage} storage</div>
                  </div>
                  <Link to="/register">
                    <Button
                      type={plan.highlight ? "primary" : "default"}
                      block
                      className="!h-10 !font-semibold"
                    >
                      Get Started
                    </Button>
                  </Link>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1f2937] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-4 gap-12 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={logoImg} alt="Kimap" className="h-6 w-auto" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional 3D indoor mapping platform for architects,
                designers, and businesses.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide mb-4 text-gray-300">
                Product
              </h4>
              <div className="space-y-2">
                <a
                  href="#features"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Pricing
                </a>
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Documentation
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide mb-4 text-gray-300">
                Support
              </h4>
              <div className="space-y-2">
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Help Center
                </a>
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Contact
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide mb-4 text-gray-300">
                Company
              </h4>
              <div className="space-y-2">
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  About
                </a>
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Privacy
                </a>
                <a
                  href="#"
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Terms
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2026 Kimap. Professional 3D Indoor Mapping Platform.
            </p>
          </div>
        </div>
      </footer>

      {/* Additional Styles */}
      <style>{`
        /* Hero Banner */
        .hero-banner {
          position: relative;
          height: 600px;
          overflow: hidden;
        }

        .banner-carousel {
          width: 100%;
          height: 100%;
        }

        .banner-slide {
          height: 600px;
          width: 100%;
        }

        .banner-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .banner-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0.6) 100%);
        }

        /* Hero Content */
        .hero-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          text-align: center;
          max-width: 800px;
          width: 100%;
          padding: 0 16px;
        }

        .hero-title {
          font-size: 56px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 24px;
          line-height: 1.1;
          letter-spacing: -1.5px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .hero-subtitle {
          font-size: 20px;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 32px;
          line-height: 1.6;
          max-width: 680px;
          margin-left: auto;
          margin-right: auto;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .hero-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-bottom: 60px;
        }

        /* Hero Stats */
        .hero-stats {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 48px;
          padding: 24px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.25);
          max-width: 500px;
          margin: 0 auto;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 36px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 4px;
          letter-spacing: -1px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .stat-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.85);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: rgba(255, 255, 255, 0.25);
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 64px;
        }

        .section-header h2 {
          font-size: 40px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 16px;
          letter-spacing: -1px;
        }

        .section-header p {
          font-size: 18px;
          color: #6b7280;
        }

        /* Popular Badge */
        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #3b82f6;
          color: white;
          padding: 4px 16px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 36px;
          }

          .hero-subtitle {
            font-size: 16px;
          }

          .hero-banner {
            height: 500px;
          }

          .banner-slide {
            height: 500px;
          }

          .hero-stats {
            flex-direction: column;
            gap: 16px;
          }

          .stat-divider {
            width: 100%;
            height: 1px;
          }

          .section-header h2 {
            font-size: 32px;
          }
        }
      `}</style>
    </div>
  );
}
